import React, { useState } from 'react';
import { PageHeader, Card, FormGroup, Input, Select, Button, DataTable, ActionButtons, SectionDivider } from '../../components/common/UIComponents';
import { AccountSubGroupController, AccountGroupController } from '../../controllers';
import { AccountHeads } from '../../models';
import { useAsyncData } from '../../hooks/useAsyncData';

export default function AccountSubGroupPage() {
  const { data: records, loading, error, reload } = useAsyncData(() => AccountSubGroupController.getAll());
  const { data: groups } = useAsyncData(() => AccountGroupController.getAll());
  const [form, setForm]   = useState({ head: '', accountGroupId: '', subGroupName: '', description: '' });
  const [editing, setEditing] = useState(null);
  const [saving, setSaving]   = useState(false);

  const filteredGroups = groups.filter(g => (g.accountHead || g.head) === form.head);

  const handleSubmit = async () => {
    if (!form.head || !form.subGroupName) return;
    setSaving(true);
    try {
      const payload = { accountHead: form.head, accountGroupId: parseInt(form.accountGroupId), subGroupName: form.subGroupName, description: form.description };
      if (editing) { await AccountSubGroupController.update(editing, payload); setEditing(null); }
      else { await AccountSubGroupController.add(payload); }
      setForm({ head: '', accountGroupId: '', subGroupName: '', description: '' });
      reload();
    } catch (e) { alert(e.message); }
    finally { setSaving(false); }
  };

  const columns = [
    { key: 'id', label: '#' },
    { key: 'accountHead',   label: 'Account Head',   render: r => r.accountHead || r.head },
    { key: 'groupName',     label: 'Group',           render: r => r.accountGroupName || r.group },
    { key: 'subGroupName',  label: 'Sub Group',       render: r => r.subGroupName || r.subGroup },
    { key: 'description',   label: 'Description' },
    { key: 'actions', label: 'Actions', render: row => (
      <ActionButtons onEdit={() => { setForm({ head: row.accountHead||row.head, accountGroupId: row.accountGroupId||'', subGroupName: row.subGroupName||row.subGroup, description: row.description||'' }); setEditing(row.id); }} onDelete={async () => { try { await AccountSubGroupController.delete?.(row.id); reload(); } catch(e){alert(e.message);} }} />
    )},
  ];

  return (
    <div className="fade-in">
      <PageHeader title="Account Sub Groups" description="Create and manage account sub groups" />
      <Card title={editing ? 'Edit Sub Group' : 'Create Sub Group'} style={{ marginBottom: 20 }}>
        <div className="form-grid form-grid-2" style={{ marginBottom: 14 }}>
          <FormGroup label="Account Head" required>
            <Select value={form.head} onChange={e => setForm(p => ({ ...p, head: e.target.value, accountGroupId: '' }))} options={AccountHeads} placeholder="Select" />
          </FormGroup>
          <FormGroup label="Account Group">
            <Select value={form.accountGroupId} onChange={e => setForm(p => ({ ...p, accountGroupId: e.target.value }))}
              options={filteredGroups.map(g => ({ value: g.id, label: g.groupName || g.group }))} placeholder="Select Group" />
          </FormGroup>
          <FormGroup label="Sub Group Name" required>
            <Input placeholder="Enter Sub Group Name" value={form.subGroupName} onChange={e => setForm(p => ({ ...p, subGroupName: e.target.value }))} />
          </FormGroup>
          <FormGroup label="Description">
            <Input placeholder="Enter Description" value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} />
          </FormGroup>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button onClick={handleSubmit} disabled={saving}>{saving ? 'Saving…' : editing ? 'Update' : 'Create Sub Group'}</Button>
          <Button variant="secondary" onClick={async () => { try { await AccountSubGroupController.applyDefaults(form); reload(); } catch(e){alert(e.message);} }}>Use Default Sub Groups</Button>
          {editing && <Button variant="ghost" onClick={() => { setForm({ head:'', accountGroupId:'', subGroupName:'', description:'' }); setEditing(null); }}>Cancel</Button>}
        </div>
      </Card>
      <SectionDivider />
      {loading && <div style={{ padding:20, color:'#78909c' }}>Loading…</div>}
      {error   && <div style={{ padding:20, color:'#c62828' }}>Error: {error}</div>}
      {!loading && <DataTable title="Account Sub Group Details" columns={columns} data={records} />}
    </div>
  );
}
